import { useMemo } from "react";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";
import type { ColDef } from "ag-grid-enterprise";
import type { HistoryEntry } from "../models";

const cellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

interface Props {
  onShowNotes: (
    show: boolean,
    entry?: HistoryEntry,
    position?: { top: number; left: number },
  ) => void;
  /** Header for the PartInfo column. Defaults to EAF format. */
  partInfoHeader?: string;
}

const DEFAULT_PART_INFO_HEADER = "Part Info (SN | Mk | Md | V)";

export const useHistoryTableData = ({
  onShowNotes,
  partInfoHeader = DEFAULT_PART_INFO_HEADER,
}: Props) => {
  const columnDefs: ColDef<HistoryEntry>[] = useMemo(
    () => [
      {
        field: "Date",
        headerName: "Date",
        minWidth: 130,
        cellStyle: cellStyle,
        sort: "desc",
        valueFormatter: (params) => {
          if (!params.value) return "-";
          return dxtToLocalServerTime(params.value, "MM/dd/yyyy HH:mm");
        },
      },
      {
        field: "LocationName",
        headerName: "Location",
        minWidth: 180,
        cellStyle: cellStyle,
        valueFormatter: (params) => params.value ?? "-",
      },
      {
        field: "Action",
        headerName: "Action",
        width: 100,
        cellStyle: cellStyle,
      },
      {
        field: "PartInfo",
        headerName: partInfoHeader,
        flex: 1,
        minWidth: 200,
        cellStyle: cellStyle,
        valueFormatter: (params) => params.value ?? "-",
      },
      {
        field: "Heats",
        headerName: "Heats",
        width: 80,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          return params.value?.toLocaleString() ?? "0";
        },
        hide: true,
      },
      {
        field: "Notes",
        headerName: "Notes",
        width: 70,
        cellStyle: cellStyle,
        cellRenderer: (params: { data: HistoryEntry }) => {
          if (!params.data?.Notes) return null;
          return (
            <CommentsColumn
              data={{
                Comments: params.data.Notes,
                ElementId: params.data.HistoryId,
              }}
              setShowOpenModal={(
                show: boolean,
                _data?: { Comments: string; ElementId: number },
                position?: { top: number; left: number },
              ) => {
                if (show) {
                  onShowNotes(true, params.data, position);
                }
              }}
            />
          );
        },
      },
    ],
    [onShowNotes, partInfoHeader],
  );

  return { columnDefs };
};
